import React, { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '../../contextos/autenticacion/ContextoAutenticacion'
import { esErrorApiAplicacion } from '../../services/apiError'
import { UsuarioCrear, RespuestaRegistro } from '../../types'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { motion } from 'framer-motion'
import {
  Mail,
  Lock,
  Eye,
  EyeOff,
  ArrowLeft,
  AlertCircle,
  Loader2,
  User,
  Phone,
  Building2,
  Sparkles,
  CheckCircle2,
  GraduationCap,
  Factory,
  UserCircle
} from 'lucide-react'
import { IsotipoLogixPort } from '../../componentes/ui/LogoLogixPort'

type TipoCuenta = 'independiente' | 'institucion' | 'empresa'

const schema = z.object({
  nombre: z.string({ required_error: 'El nombre es requerido' })
    .min(2, 'El nombre debe tener al menos 2 caracteres'),
  apellido: z.string({ required_error: 'El apellido es requerido' })
    .min(2, 'El apellido debe tener al menos 2 caracteres'),
  email: z.string({ required_error: 'El correo electrónico es requerido' })
    .email('Ingresa un correo electrónico válido'),
  telefono: z.string().optional(),
  empresa: z.string().optional(),
  password: z.string({ required_error: 'La contraseña es requerida' })
    .min(6, 'La contraseña debe tener al menos 6 caracteres'),
  confirmPassword: z.string({ required_error: 'Confirma tu contraseña' }),
  tipoCuenta: z.enum(['independiente', 'institucion', 'empresa'], {
    required_error: 'Selecciona el tipo de cuenta',
  }),
  nombre_organizacion: z.string().optional(),
}).superRefine((data, ctx) => {
  if (data.password !== data.confirmPassword) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Las contraseñas no coinciden',
      path: ['confirmPassword'],
    })
  }
  if ((data.tipoCuenta === 'institucion' || data.tipoCuenta === 'empresa') && !data.nombre_organizacion?.trim()) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Captura el nombre de tu organización',
      path: ['nombre_organizacion'],
    })
  }
})

type FormularioRegistro = z.infer<typeof schema>

const opcionesTipoCuenta: {
  value: TipoCuenta;
  titulo: string;
  descripcion: string;
  badge: string;
  badgeColor: string;
  icono: React.ElementType;
  color: string;
  destino: string;
  esDesarrollo?: boolean;
}[] = [
  {
    value: 'institucion',
    titulo: 'Institución / Campus',
    descripcion: 'Directores y Docentes',
    badge: 'ACTIVO',
    badgeColor: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20',
    icono: GraduationCap,
    color: 'from-blue-600 to-cyan-500',
    destino: '/institucion/jefes',
    esDesarrollo: false,
  },
  {
    value: 'empresa',
    titulo: 'Empresa / CEDIS',
    descripcion: 'Operadores 3PL',
    badge: 'EN DESARROLLO',
    badgeColor: 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20',
    icono: Factory,
    color: 'from-amber-500 to-orange-600',
    destino: '/empresa/gestion',
    esDesarrollo: true,
  },
  {
    value: 'independiente',
    titulo: 'Independiente',
    descripcion: 'Uso personal',
    badge: 'BÁSICO',
    badgeColor: 'bg-slate-100 text-slate-600 dark:bg-white/5 dark:text-zinc-400 border-slate-200 dark:border-white/10',
    icono: UserCircle,
    color: 'from-zinc-400 to-slate-500',
    destino: '/dashboard',
    esDesarrollo: false,
  },
]

export const PaginaRegistro: React.FC = () => {
  const { register: registrarUsuario, isLoading } = useAuth()
  const navigate = useNavigate()

  const { register, handleSubmit, formState: { errors, isSubmitting }, watch } = useForm<FormularioRegistro>({
    resolver: zodResolver(schema),
    defaultValues: {
      nombre: '',
      apellido: '',
      email: '',
      telefono: '',
      empresa: '',
      password: '',
      confirmPassword: '',
      tipoCuenta: 'independiente',
      nombre_organizacion: '',
    },
  })

  const tipoCuentaSeleccionado = watch('tipoCuenta') as TipoCuenta
  const requiereOrganizacion = tipoCuentaSeleccionado === 'institucion' || tipoCuentaSeleccionado === 'empresa'

  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [generalError, setGeneralError] = useState<string | null>(null)
  const [registroExitoso, setRegistroExitoso] = useState<null | RespuestaRegistro>(null)

  const rutaPanelPorTipo = (tipo: TipoCuenta) =>
    opcionesTipoCuenta.find((op) => op.value === tipo)?.destino ?? '/dashboard'

  const onSubmit = async (data: FormularioRegistro) => {
    setGeneralError(null)
    try {
      const tipoOrg = data.tipoCuenta === 'independiente' ? undefined : data.tipoCuenta as 'empresa' | 'institucion'
      const nombreOrg = data.nombre_organizacion?.trim() || undefined
      const nombreOrgFallback =
        !nombreOrg && data.tipoCuenta === 'empresa' ? `${data.apellido} ${data.nombre} Empresa` :
        !nombreOrg && data.tipoCuenta === 'institucion' ? `${data.apellido} ${data.nombre} Academia` : undefined

      const payload: UsuarioCrear = {
        nombre: data.nombre.trim(),
        apellido: data.apellido.trim(),
        email: data.email.trim().toLowerCase(),
        password: data.password,
        telefono: data.telefono?.trim() || undefined,
        empresa: (data.tipoCuenta === 'empresa' ? (nombreOrg ?? nombreOrgFallback) : data.empresa?.trim()) || undefined,
        tipo_organizacion: tipoOrg,
        nombre_organizacion: nombreOrg ?? nombreOrgFallback,
      }
      const resultado = await registrarUsuario(payload)
      setRegistroExitoso(resultado)
      setTimeout(() => {
        navigate('/login')
      }, 3500)
    } catch (error: unknown) {
      const msg =
        esErrorApiAplicacion(error) && error.code === 'conflict'
          ? 'El correo electrónico ya se encuentra registrado.'
          : esErrorApiAplicacion(error) && error.code === 'validation_error'
            ? 'Verifica los datos capturados e inténtalo nuevamente.'
            : error instanceof Error
              ? error.message
              : 'No se pudo crear la cuenta en este momento.'
      setGeneralError(msg)
    }
  }

  if (registroExitoso) {
    const opcionElegida = opcionesTipoCuenta.find(
      (op) =>
        op.value === (registroExitoso.tipo_organizacion ?? (tipoCuentaSeleccionado === 'independiente' ? 'independiente' : null)) ||
        (!registroExitoso.tipo_organizacion && op.value === 'independiente')
    ) ?? opcionesTipoCuenta[0]
    const IconoExito = opcionElegida.icono
    const rutaSugerida = rutaPanelPorTipo(tipoCuentaSeleccionado)

    return (
      <div className="min-h-screen bg-slate-50 dark:bg-[#030303] dark:text-zinc-100 relative flex items-center justify-center overflow-hidden selection:bg-cyan-500/30">
        <div className="absolute inset-0 pointer-events-none z-0">
          <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px] dark:bg-[linear-gradient(to_right,#ffffff05_1px,transparent_1px),linear-gradient(to_bottom,#ffffff05_1px,transparent_1px)]" />
          <div className="absolute top-[-10%] left-[-10%] w-[60%] h-[40%] rounded-full bg-cyan-500/20 blur-[100px] md:blur-[120px] mix-blend-screen dark:bg-cyan-500/10" />
          <div className="absolute bottom-[-10%] right-[-10%] w-[60%] h-[40%] rounded-full bg-violet-500/20 blur-[100px] md:blur-[120px] mix-blend-screen dark:bg-violet-500/10" />
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
          className="relative z-10 w-full max-w-lg mx-6"
        >
          <div className="rounded-[2rem] border border-emerald-500/30 bg-white/70 p-8 sm:p-10 shadow-2xl backdrop-blur-xl dark:border-white/10 dark:bg-white/5 text-center">
            <div className={`mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-gradient-to-br ${opcionElegida.color} text-white shadow-lg`}>
              <IconoExito className="h-10 w-10" />
            </div>
            <div className="inline-flex items-center gap-2 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-emerald-700 dark:text-emerald-400 mb-4">
              <CheckCircle2 className="h-3 w-3" />
              Registro completado
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 dark:text-white tracking-tight mb-3">
              ¡Cuenta creada!
            </h2>
            {tipoCuentaSeleccionado === 'institucion' ? (
              <>
                <p className="text-sm text-amber-600 dark:text-amber-400 mb-2 font-medium">
                  Tu cuenta ha sido creada y enviada a revisión.
                </p>
                <div className="rounded-xl border border-amber-500/20 bg-amber-500/10 p-4 text-sm text-amber-700 dark:text-amber-400 mb-6 flex flex-col items-center gap-2">
                  <AlertCircle className="h-6 w-6 shrink-0" />
                  <p>
                    Un administrador de LogiXport validará tu institución. Recibirás acceso completo una vez aprobada.
                  </p>
                </div>
              </>
            ) : (
              <>
                <p className="text-sm text-slate-600 dark:text-zinc-400 mb-2">
                  Tu plan <span className="font-bold text-cyan-600 dark:text-cyan-400">Básico</span> está activo · Tipo: <span className="font-bold text-slate-800 dark:text-zinc-200">{opcionElegida.titulo}</span>
                </p>
                {registroExitoso.tipo_organizacion && registroExitoso.rol_organizacion && (
                  <p className="text-xs text-slate-500 dark:text-zinc-500 mb-2">
                    Rol asignado: <span className="font-semibold">{registroExitoso.rol_organizacion.toUpperCase()}</span>
                  </p>
                )}
                <p className="text-sm text-slate-500 dark:text-zinc-500 mb-6">
                  Al iniciar sesión se te enviará a <span className="font-mono text-[11px] bg-slate-200 dark:bg-white/10 px-2 py-0.5 rounded">{rutaSugerida}</span>
                </p>
              </>
            )}
            <Link
              to="/login"
              className="inline-flex h-12 items-center justify-center gap-2 rounded-full bg-gradient-to-r from-cyan-500 to-blue-600 px-8 text-xs font-bold uppercase tracking-widest text-white transition-all hover:scale-105 hover:shadow-[0_0_40px_-10px_rgba(6,182,212,0.6)] w-full"
            >
              Ir al inicio de sesión
            </Link>
          </div>
        </motion.div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 dark:bg-[#030303] dark:text-zinc-100 relative flex items-center justify-center overflow-hidden selection:bg-cyan-500/30">

      <div className="absolute inset-0 pointer-events-none z-0">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px] dark:bg-[linear-gradient(to_right,#ffffff05_1px,transparent_1px),linear-gradient(to_bottom,#ffffff05_1px,transparent_1px)]" />
        <div className="absolute top-[-10%] left-[-10%] w-[60%] h-[40%] md:w-[40%] md:h-[40%] rounded-full bg-cyan-500/20 blur-[100px] md:blur-[120px] mix-blend-screen dark:bg-cyan-500/10" />
        <div className="absolute bottom-[-10%] right-[-10%] w-[60%] h-[40%] md:w-[40%] md:h-[40%] rounded-full bg-violet-500/20 blur-[100px] md:blur-[120px] mix-blend-screen dark:bg-violet-500/10" />
      </div>

      <header className="absolute top-6 left-0 right-0 z-50 w-full max-w-7xl mx-auto flex justify-between px-6 md:px-12">
        <Link
          to="/login"
          className="group flex items-center gap-2 rounded-full border border-slate-200/50 bg-white/70 px-4 py-2 backdrop-blur-xl shadow-sm dark:border-white/10 dark:bg-black/50 transition-all hover:bg-white dark:hover:bg-black"
        >
          <ArrowLeft className="h-4 w-4 text-slate-500 transition-transform group-hover:-translate-x-1 dark:text-zinc-400" />
          <span className="text-xs font-bold uppercase tracking-widest text-slate-700 dark:text-zinc-300">
            Ya tengo cuenta
          </span>
        </Link>
      </header>

      <div className="relative z-10 w-full max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24 px-6 md:px-12 py-24">

        <div className="hidden lg:flex flex-col justify-center">
          <Link to="/" className="flex items-center gap-3 mb-8 group">
            <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-gradient-to-tr from-slate-950 via-slate-900 to-blue-950 p-1.5 shadow-lg shadow-cyan-500/10 border border-slate-800/80 group-hover:scale-105 transition-all duration-300 dark:from-white/10 dark:via-white/5 dark:to-cyan-500/10 dark:border-white/15">
              <IsotipoLogixPort sizeCls="w-full h-full" />
            </div>
            <div>
              <span className="text-2xl font-black tracking-tight text-slate-900 dark:text-white flex items-center gap-2">
                Logix<span className="text-cyan-600 dark:text-cyan-400">Port</span>
                <span className="text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded-md bg-cyan-500/10 text-cyan-600 dark:text-cyan-400 border border-cyan-500/20">v1.0</span>
              </span>
              <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400 dark:text-zinc-500">
                Logistics & Digital Twin OS
              </p>
            </div>
          </Link>

          <div className="max-w-md space-y-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <div className="inline-flex items-center gap-2 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-3.5 py-1 text-[10px] font-black uppercase tracking-wider text-emerald-600 dark:text-emerald-400 mb-4">
                <Sparkles className="h-3.5 w-3.5" />
                <span>REGISTRO INSTITUCIONAL Y PERSONAL</span>
              </div>

              <h1 className="text-4xl lg:text-5xl font-black tracking-tight text-slate-900 dark:text-white leading-[1.08] mb-4">
                Comienza con <br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-blue-500 to-indigo-500">
                  LogixPort v1.0.
                </span>
              </h1>
              <p className="text-sm lg:text-base text-slate-600 dark:text-zinc-400 leading-relaxed font-normal">
                Configura tu entorno de simulación para universidades o inicia tus pruebas personales de almacén y aduanas.
              </p>
            </motion.div>

            <ul className="space-y-3">
              {[
                'Campus Edition 100% activo y operativo',
                'Almacén 3D y trazabilidad física GS1-128',
                'Clasificación arancelaria LIGIE en tiempo real',
                'LogixPort Empresarial en fase beta cerrada',
              ].map((item, idx) => (
                <motion.li
                  key={item}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: 0.2 + idx * 0.1 }}
                  className="flex items-center gap-3 text-sm text-slate-700 dark:text-zinc-300"
                >
                  <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-cyan-500/15">
                    <CheckCircle2 className="h-3.5 w-3.5 text-cyan-500" />
                  </span>
                  {item}
                </motion.li>
              ))}
            </ul>
          </div>
        </div>

        <div className="flex items-center justify-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
            className="w-full max-w-md"
          >
            <div className="rounded-[2rem] border border-slate-200/80 bg-white/80 p-6 sm:p-8 shadow-2xl backdrop-blur-2xl dark:border-white/10 dark:bg-[#0c0c0c]/85">

              <div className="text-center mb-5">
                <h2 className="text-2xl sm:text-3xl font-black text-slate-900 dark:text-white tracking-tight mb-1">
                  Crear cuenta
                </h2>
                <p className="text-xs sm:text-sm text-slate-600 dark:text-zinc-400">
                  Selecciona el tipo de cuenta para configurar tu panel
                </p>
              </div>

              {/* Guía Rápida para Alumnos */}
              <div className="mb-5 rounded-2xl border border-blue-500/25 bg-blue-500/10 p-3 text-xs text-slate-700 dark:text-zinc-300 flex items-start gap-2.5">
                <GraduationCap className="h-4.5 w-4.5 shrink-0 text-blue-600 dark:text-cyan-400 mt-0.5" />
                <div>
                  <span className="font-bold text-slate-900 dark:text-white">¿Eres estudiante o docente invitado?</span>
                  <p className="mt-0.5 text-[11px] leading-snug text-slate-600 dark:text-zinc-400">
                    Tu institución ya te dio de alta en su campus. No necesitas crear cuenta nueva; <Link to="/login" className="font-bold text-blue-600 dark:text-cyan-400 underline ml-1">inicia sesión directamente aquí</Link>.
                  </p>
                </div>
              </div>

              <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">

                {/* SELECTOR TIPO DE CUENTA */}
                <div className="space-y-2">
                  <label className="block text-xs font-bold uppercase tracking-widest text-slate-700 dark:text-zinc-300">
                    Tipo de cuenta *
                  </label>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                    {opcionesTipoCuenta.map((op) => {
                      const Icono = op.icono
                      const seleccionado = tipoCuentaSeleccionado === op.value
                      return (
                        <label
                          key={op.value}
                          className={`relative cursor-pointer rounded-xl border p-2.5 transition-all duration-200 select-none flex flex-col justify-between ${
                            seleccionado
                              ? 'border-transparent ring-2 ring-cyan-500 bg-gradient-to-br from-white to-slate-50 dark:from-white/10 dark:to-white/5 shadow-md'
                              : 'border-slate-200/50 bg-white/50 hover:border-slate-300 dark:border-white/10 dark:bg-black/30 dark:hover:bg-black/50'
                          }`}
                        >
                          <input
                            type="radio"
                            value={op.value}
                            {...register('tipoCuenta')}
                            className="sr-only"
                          />
                          <div className="text-center">
                            <span className={`text-[7.5px] font-black uppercase px-1.5 py-0.5 rounded-full border mb-1.5 inline-block ${op.badgeColor}`}>
                              {op.badge}
                            </span>
                            <div className={`mx-auto mb-1.5 flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br ${op.color} text-white shadow-sm`}>
                              <Icono className="h-4 w-4" />
                            </div>
                            <p className="text-[11px] font-black text-center text-slate-900 dark:text-white leading-tight mb-0.5">
                              {op.titulo}
                            </p>
                            <p className="text-[9.5px] text-center text-slate-500 dark:text-zinc-500 leading-tight">
                              {op.descripcion}
                            </p>
                          </div>
                          {seleccionado && (
                            <div className="absolute top-1.5 right-1.5 h-3.5 w-3.5 rounded-full bg-cyan-500 flex items-center justify-center">
                              <CheckCircle2 className="h-2.5 w-2.5 text-white" />
                            </div>
                          )}
                        </label>
                      )
                    })}
                  </div>
                </div>

                {/* Nota para Empresa si está seleccionada */}
                {tipoCuentaSeleccionado === 'empresa' && (
                  <div className="rounded-xl border border-amber-500/25 bg-amber-500/10 p-3 text-xs text-amber-700 dark:text-amber-400 flex items-start gap-2">
                    <Factory className="h-4 w-4 shrink-0 text-amber-500 mt-0.5" />
                    <p className="text-[11px] leading-relaxed">
                      <strong>LogixPort Empresarial (Beta):</strong> El módulo corporativo está actualmente en desarrollo. Al registrarte podrás previsualizar la estructura básica de empresa.
                    </p>
                  </div>
                )}

                {tipoCuentaSeleccionado === 'institucion' ? (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    className="overflow-hidden"
                  >
                    <div className="rounded-xl border border-cyan-500/20 bg-cyan-500/10 p-5 text-center flex flex-col items-center gap-3 my-4">
                      <GraduationCap className="h-8 w-8 text-cyan-600 dark:text-cyan-400" />
                      <h3 className="text-sm font-bold text-slate-900 dark:text-white">Registro Institucional Seguro</h3>
                      <p className="text-xs text-slate-600 dark:text-zinc-400 leading-relaxed">
                        Para habilitar un campus universitario o escuela y validar la legitimidad académica, nuestro equipo realiza una validación rápida.
                      </p>
                      <a href="mailto:contacto@logixport.dev" className="mt-1 inline-flex items-center gap-2 rounded-full bg-slate-900 dark:bg-white px-5 py-2 text-xs font-bold text-white dark:text-slate-900 transition-transform hover:scale-105">
                        <Mail className="h-4 w-4" />
                        Solicitar Activación de Campus
                      </a>
                    </div>
                  </motion.div>
                ) : (
                  <>
                    {/* Nombre Organización */}
                    {requiereOrganizacion && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="overflow-hidden space-y-1.5"
                      >
                        <label htmlFor="nombre_organizacion" className="block text-xs font-bold uppercase tracking-widest text-slate-700 dark:text-zinc-300">
                          Nombre de la Empresa *
                        </label>
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                            <Building2 className="h-4 w-4 text-slate-400 dark:text-zinc-500" />
                          </div>
                          <input
                            type="text"
                            id="nombre_organizacion"
                            {...register('nombre_organizacion')}
                            className={`block w-full rounded-xl border bg-white/50 py-3 pl-11 pr-4 text-sm text-slate-900 placeholder-slate-400 backdrop-blur-sm transition-all focus:border-cyan-500 focus:outline-none focus:ring-1 focus:ring-cyan-500 dark:bg-black/50 dark:text-white dark:placeholder-zinc-600 ${
                              errors.nombre_organizacion?.message ? 'border-red-500/50 focus:border-red-500 focus:ring-red-500' : 'border-slate-200/50 dark:border-white/10'
                            }`}
                            placeholder="Mi Empresa S.A. de C.V."
                          />
                        </div>
                        {errors.nombre_organizacion?.message && (
                          <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                            <AlertCircle className="h-3 w-3" /> {errors.nombre_organizacion.message}
                          </p>
                        )}
                      </motion.div>
                    )}

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-1.5">
                        <label htmlFor="nombre" className="block text-xs font-bold uppercase tracking-widest text-slate-700 dark:text-zinc-300">
                          Nombre *
                        </label>
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                            <User className="h-4 w-4 text-slate-400 dark:text-zinc-500" />
                          </div>
                          <input
                            type="text"
                            id="nombre"
                            {...register('nombre')}
                            className={`block w-full rounded-xl border bg-white/50 py-3 pl-11 pr-4 text-sm text-slate-900 placeholder-slate-400 backdrop-blur-sm transition-all focus:border-cyan-500 focus:outline-none focus:ring-1 focus:ring-cyan-500 dark:bg-black/50 dark:text-white dark:placeholder-zinc-600 ${
                              errors.nombre ? 'border-red-500/50 focus:border-red-500 focus:ring-red-500' : 'border-slate-200/50 dark:border-white/10'
                            }`}
                            placeholder="Juan"
                          />
                        </div>
                        {errors.nombre?.message && (
                          <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                            <AlertCircle className="h-3 w-3" /> {errors.nombre.message}
                          </p>
                        )}
                      </div>

                      <div className="space-y-1.5">
                        <label htmlFor="apellido" className="block text-xs font-bold uppercase tracking-widest text-slate-700 dark:text-zinc-300">
                          Apellido *
                        </label>
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                            <User className="h-4 w-4 text-slate-400 dark:text-zinc-500" />
                          </div>
                          <input
                            type="text"
                            id="apellido"
                            {...register('apellido')}
                            className={`block w-full rounded-xl border bg-white/50 py-3 pl-11 pr-4 text-sm text-slate-900 placeholder-slate-400 backdrop-blur-sm transition-all focus:border-cyan-500 focus:outline-none focus:ring-1 focus:ring-cyan-500 dark:bg-black/50 dark:text-white dark:placeholder-zinc-600 ${
                              errors.apellido ? 'border-red-500/50 focus:border-red-500 focus:ring-red-500' : 'border-slate-200/50 dark:border-white/10'
                            }`}
                            placeholder="Pérez"
                          />
                        </div>
                        {errors.apellido?.message && (
                          <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                            <AlertCircle className="h-3 w-3" /> {errors.apellido.message}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="space-y-1.5">
                      <label htmlFor="email" className="block text-xs font-bold uppercase tracking-widest text-slate-700 dark:text-zinc-300">
                        Correo electrónico *
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                          <Mail className="h-4 w-4 text-slate-400 dark:text-zinc-500" />
                        </div>
                        <input
                          type="email"
                          id="email"
                          {...register('email')}
                          className={`block w-full rounded-xl border bg-white/50 py-3 pl-11 pr-4 text-sm text-slate-900 placeholder-slate-400 backdrop-blur-sm transition-all focus:border-cyan-500 focus:outline-none focus:ring-1 focus:ring-cyan-500 dark:bg-black/50 dark:text-white dark:placeholder-zinc-600 ${
                            errors.email ? 'border-red-500/50 focus:border-red-500 focus:ring-red-500' : 'border-slate-200/50 dark:border-white/10'
                          }`}
                          placeholder="tu@email.com"
                        />
                      </div>
                      {errors.email?.message && (
                        <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                          <AlertCircle className="h-3 w-3" /> {errors.email.message}
                        </p>
                      )}
                    </div>

                    <div className="space-y-1.5">
                      <label htmlFor="telefono" className="block text-xs font-bold uppercase tracking-widest text-slate-700 dark:text-zinc-300">
                        Teléfono
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                          <Phone className="h-4 w-4 text-slate-400 dark:text-zinc-500" />
                        </div>
                        <input
                          type="tel"
                          id="telefono"
                          {...register('telefono')}
                          className="block w-full rounded-xl border border-slate-200/50 bg-white/50 py-3 pl-11 pr-4 text-sm text-slate-900 placeholder-slate-400 backdrop-blur-sm transition-all focus:border-cyan-500 focus:outline-none focus:ring-1 focus:ring-cyan-500 dark:border-white/10 dark:bg-black/50 dark:text-white dark:placeholder-zinc-600"
                          placeholder="+52 55 1234 5678"
                        />
                      </div>
                    </div>

                    <div className="space-y-1.5">
                      <label htmlFor="password" className="block text-xs font-bold uppercase tracking-widest text-slate-700 dark:text-zinc-300">
                        Contraseña *
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                          <Lock className="h-4 w-4 text-slate-400 dark:text-zinc-500" />
                        </div>
                        <input
                          type={showPassword ? 'text' : 'password'}
                          id="password"
                          {...register('password')}
                          className={`block w-full rounded-xl border bg-white/50 py-3 pl-11 pr-11 text-sm text-slate-900 placeholder-slate-400 backdrop-blur-sm transition-all focus:border-cyan-500 focus:outline-none focus:ring-1 focus:ring-cyan-500 dark:bg-black/50 dark:text-white dark:placeholder-zinc-600 ${
                            errors.password ? 'border-red-500/50 focus:border-red-500 focus:ring-red-500' : 'border-slate-200/50 dark:border-white/10'
                          }`}
                          placeholder="••••••••"
                          disabled={isSubmitting || isLoading}
                        />
                        <button
                          type="button"
                          className="absolute inset-y-0 right-0 pr-4 flex items-center text-slate-400 hover:text-cyan-500 dark:text-zinc-500 dark:hover:text-cyan-400 transition-colors"
                          onClick={() => setShowPassword(!showPassword)}
                          disabled={isSubmitting || isLoading}
                        >
                          {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                      {errors.password?.message && (
                        <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                          <AlertCircle className="h-3 w-3" /> {errors.password.message}
                        </p>
                      )}
                    </div>

                    <div className="space-y-1.5">
                      <label htmlFor="confirmPassword" className="block text-xs font-bold uppercase tracking-widest text-slate-700 dark:text-zinc-300">
                        Confirmar contraseña *
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                          <Lock className="h-4 w-4 text-slate-400 dark:text-zinc-500" />
                        </div>
                        <input
                          type={showConfirmPassword ? 'text' : 'password'}
                          id="confirmPassword"
                          {...register('confirmPassword')}
                          className={`block w-full rounded-xl border bg-white/50 py-3 pl-11 pr-11 text-sm text-slate-900 placeholder-slate-400 backdrop-blur-sm transition-all focus:border-cyan-500 focus:outline-none focus:ring-1 focus:ring-cyan-500 dark:bg-black/50 dark:text-white dark:placeholder-zinc-600 ${
                            errors.confirmPassword ? 'border-red-500/50 focus:border-red-500 focus:ring-red-500' : 'border-slate-200/50 dark:border-white/10'
                          }`}
                          placeholder="••••••••"
                          disabled={isSubmitting || isLoading}
                        />
                        <button
                          type="button"
                          className="absolute inset-y-0 right-0 pr-4 flex items-center text-slate-400 hover:text-cyan-500 dark:text-zinc-500 dark:hover:text-cyan-400 transition-colors"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          disabled={isSubmitting || isLoading}
                        >
                          {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                      {errors.confirmPassword?.message && (
                        <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                          <AlertCircle className="h-3 w-3" /> {errors.confirmPassword.message}
                        </p>
                      )}
                    </div>

                    {generalError && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="overflow-hidden"
                      >
                        <div className="rounded-xl border border-red-500/20 bg-red-500/10 p-3 text-center text-xs font-medium text-red-600 dark:text-red-400 flex items-center justify-center gap-2">
                          <AlertCircle className="h-4 w-4" />
                          {generalError}
                        </div>
                      </motion.div>
                    )}

                    <div className="rounded-xl border border-emerald-500/20 bg-emerald-500/5 p-3 flex items-start gap-2">
                      <Sparkles className="h-4 w-4 text-emerald-500 shrink-0 mt-0.5" />
                      <p className="text-[11px] text-emerald-700 dark:text-emerald-400 leading-relaxed">
                        <span className="font-bold">Sin pago inicial.</span> Plan Básico y rol <span className="font-bold">
                          {tipoCuentaSeleccionado === 'independiente'
                            ? 'Usuario'
                            : `Admin de Empresa`}
                        </span> asignados gratis.
                      </p>
                    </div>

                    <button
                      type="submit"
                      disabled={isSubmitting || isLoading}
                      className="group relative flex w-full items-center justify-center gap-2 overflow-hidden rounded-xl bg-gradient-to-r from-cyan-500 to-blue-600 px-8 py-3.5 text-sm font-bold uppercase tracking-widest text-white transition-all hover:scale-[1.02] hover:shadow-[0_0_40px_-10px_rgba(6,182,212,0.6)] disabled:opacity-70 disabled:hover:scale-100"
                    >
                      {isSubmitting || isLoading ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" />
                          <span>Creando cuenta...</span>
                        </>
                      ) : (
                        <span>Crear cuenta gratis</span>
                      )}
                    </button>
                  </>
                )}

                <div className="relative flex items-center justify-center py-3">
                  <div className="w-full border-t border-slate-200/50 dark:border-white/10" />
                  <span className="absolute bg-white px-3 text-[10px] font-bold uppercase tracking-widest text-slate-400 dark:bg-[#0a0a0a] dark:text-zinc-500">
                    ¿Ya tienes cuenta?
                  </span>
                </div>

                <div className="text-center">
                  <Link
                    to="/login"
                    className="text-sm font-medium text-slate-600 dark:text-zinc-400 hover:text-cyan-500 dark:hover:text-cyan-400 transition-colors"
                  >
                    Inicia sesión en <span className="font-bold">LogiXport</span>
                  </Link>
                </div>
              </form>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  )
}

export default PaginaRegistro
